// The "Teams" panel of the settings dialog (RFC-1371, RFC-1372 C9).
//
// One component, several views driven by state, exactly like
// UsageAndCreditsSection. That is not a stylistic echo — it is forced. This
// panel lives INSIDE `SettingsModal`, and SettingsModal.tsx:103 records why a
// second live `<Modal>` is not allowed: two dialogs each install an Escape
// handler and a body-scroll lock, so one Escape dismisses both and closing one
// unlocks scrolling behind the other. Create, leave and delete are therefore
// in-panel views rather than nested confirmations.
//
// ── What each role sees ────────────────────────────────────────────────────
//
// Every gate routes through features/teams/permissions and HIDES the control
// rather than disabling it (§6: "a Member must not see controls they cannot
// use"). Concretely, in a `kind='team'` team:
//
//   Owner   team name is editable, invites, roles, Leave, Delete
//   Admin   invites, roles (never to/from Owner), Leave. No rename, no delete.
//   Member  the roster read-only, the credit history, Leave. Nothing else.
//
// A personal team is now the same panel with two subtractions, not a different
// panel. Its Owner can rename it, invite people, manage the roster and set
// allowances exactly as in a `kind='team'` team. What it still cannot do is be
// deleted, or be left BY ITS OWNER (D10, narrowed) — outliving the account is
// the point of it, and a departure would appoint a successor and hand the
// account's own scope away. Members the Owner invited leave normally.
//
// The credit history is deliberately outside all of that — C9 makes it visible
// to every member, and the reasoning is in TeamCreditHistory.tsx.

import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Plus, User, Users } from 'lucide-react'

import type {
  SettingsViewEntryPoint,
  TeamCreateEntryPoint,
} from '../../analytics/generated/analyticsEvents'
import type { Team } from '../../api/teams'
import { useActiveTeam } from '../../contexts/ActiveTeamContext'
import { useAuth } from '../../contexts/AuthContext'
import {
  canBuyCredits,
  canDeleteTeam,
  canLeaveTeam,
  canManageMembers,
  canManageTeamSettings,
} from '../../features/teams/permissions'
import { useTeamMembers } from '../../hooks/useTeams'
import { useBillingVisible } from '../../features/billing/useBillingVisible'
import { useTeamsEnabled } from '../../features/teams/useTeamsEnabled'
import { cn } from '../../lib/utils'
import { useSettingsModal } from '../settings/settings-modal/SettingsModalProvider'
import CreateTeamView from './CreateTeamView'
import DeleteTeamView from './DeleteTeamView'
import LeaveTeamView from './LeaveTeamView'
import TeamCreditHistory from './TeamCreditHistory'
import { canViewTeamCredits } from '../../features/billing/creditVisibility'
import { canViewPostpaidUsage, isPostpaidTeam } from '../../features/billing/teamBillingMode'
import TeamInvitePanel from './TeamInvitePanel'
import TeamMembersList from './TeamMembersList'
import TeamNameField from './TeamNameField'
import { Card, EmptyState, SecondaryButton, SectionLabel } from './teamsChrome'

type View = 'overview' | 'create' | 'leave' | 'delete'

export default function TeamsSection({
  entryPoint,
}: {
  /**
   * How Settings → Teams came on screen, for the invite events' entry_point
   * (RFC-1577). SettingsModal always passes it; without it none is logged.
   */
  entryPoint?: SettingsViewEntryPoint
}) {
  const { t } = useTranslation('settings')
  const { user } = useAuth()
  const { teams, activeTeam, setActiveTeamId, isLoading } = useActiveTeam()
  // The profile menu's "Create team" row opens this panel straight into the
  // create form. Seeded into the initial state so the overview never flashes
  // first, then consumed so reopening Settings later lands on the overview.
  const { teamsView: requestedView, clearTeamsView, open } = useSettingsModal()
  const billingVisible = useBillingVisible()
  const teamsEnabled = useTeamsEnabled()
  const [view, setView] = useState<View>(requestedView ?? 'overview')
  // Where the create form was opened from, for `create_team_clicked`. The
  // flyout's direct landing is the only non-Settings entry; every in-panel
  // "Create team" link resets it to `settings`.
  const [createEntryPoint, setCreateEntryPoint] = useState<TeamCreateEntryPoint>(
    requestedView === 'create' ? 'profile_flyout' : 'settings',
  )
  useEffect(() => {
    if (requestedView === undefined) return
    setView(requestedView)
    if (requestedView === 'create') setCreateEntryPoint('profile_flyout')
    clearTeamsView()
  }, [requestedView, clearTeamsView])
  const openCreate = () => {
    setCreateEntryPoint('settings')
    setView('create')
  }

  // `entryPoint` is latched at mount (the section remounts per activation):
  // the prop can change under us once TeamsSection consumes the flyout's
  // requested view.
  const mountEntryPointRef = useRef(entryPoint)
  // Which team the PANEL is showing. Starts at the active team but is not
  // pinned to it: reviewing another team's roster should not silently change
  // where your next project gets created (D2 makes that irreversible).
  const [selectedTeamId, setSelectedTeamId] = useState<string | null>(null)

  const selectedTeam: Team | null =
    teams.find((team) => team.id === selectedTeamId) ?? activeTeam

  // Follow the active team until the user picks something else here. Without
  // this the panel would keep showing a team the user switched away from in
  // the header, which reads as the switcher not working.
  useEffect(() => {
    if (selectedTeamId === null && activeTeam !== null) {
      setSelectedTeamId(activeTeam.id)
    }
  }, [activeTeam, selectedTeamId])

  // A team that vanished under us — deleted, or we were removed from it. Fall
  // back rather than rendering an overview of nothing.
  useEffect(() => {
    if (selectedTeamId !== null && !teams.some((team) => team.id === selectedTeamId)) {
      setSelectedTeamId(null)
      setView('overview')
    }
  }, [teams, selectedTeamId])

  const members = useTeamMembers(selectedTeam?.id ?? null)

  if (isLoading && teams.length === 0) {
    return (
      <EmptyState testId="teams-section-loading">
        {t('settingsModal.teams.loadingTeams')}
      </EmptyState>
    )
  }

  if (view === 'create') {
    return (
      <CreateTeamView
        entryPoint={createEntryPoint}
        onCancel={() => setView('overview')}
        onCreated={(team) => {
          setActiveTeamId(team.id)
          setSelectedTeamId(team.id)
          setView('overview')
        }}
      />
    )
  }

  if (view === 'leave' && selectedTeam !== null) {
    return (
      <LeaveTeamView
        team={selectedTeam}
        onCancel={() => setView('overview')}
        onDone={() => {
          setSelectedTeamId(null)
          setView('overview')
        }}
      />
    )
  }

  if (view === 'delete' && selectedTeam !== null) {
    return (
      <DeleteTeamView
        team={selectedTeam}
        onCancel={() => setView('overview')}
        onDone={() => {
          setSelectedTeamId(null)
          setView('overview')
        }}
      />
    )
  }

  if (selectedTeam === null) {
    return (
      <div className="flex flex-col gap-4" data-testid="teams-section-empty">
        <EmptyState testId="teams-section-no-teams">
          {t('settingsModal.teams.noTeams')}
        </EmptyState>
        <div className="flex justify-end">
          <SecondaryButton testId="teams-create-open" onClick={openCreate}>
            {t('settingsModal.teams.createTeam')}
          </SecondaryButton>
        </div>
      </div>
    )
  }

  const ctx = { role: selectedTeam.myRole, kind: selectedTeam.kind }
  // No `kind` term any more: a personal team is invitable, and hiding the panel
  // there was the only thing stopping it.
  const showInvites = canManageMembers(ctx)
  const showRename = canManageTeamSettings(ctx)
  const showLeave = canLeaveTeam(ctx)
  const showDelete = canDeleteTeam(ctx)
  const postpaid = isPostpaidTeam(selectedTeam)
  const showBuyCredits = teamsEnabled && billingVisible && !postpaid && canBuyCredits(ctx)
  const showUsage = canViewPostpaidUsage(selectedTeam)

  return (
    <div className="flex flex-col gap-6" data-testid="teams-section">
      <div>
        <SectionLabel>{t('settingsModal.teams.yourTeams')}</SectionLabel>
        <Card>
          {teams.map((team, index) => {
            const isSelected = team.id === selectedTeam.id
            const isActive = team.id === activeTeam?.id
            return (
              <button
                key={team.id}
                type="button"
                data-testid={`teams-list-item-${team.id}`}
                aria-current={isSelected}
                onClick={() => setSelectedTeamId(team.id)}
                className={cn(
                  'flex w-full cursor-pointer items-center gap-3 px-4 py-3 text-left transition-colors',
                  index > 0 && 'border-t border-white/[0.06]',
                  isSelected ? 'bg-white/[0.08]' : 'hover:bg-white/[0.04]',
                )}
              >
                {team.kind === 'personal' ? (
                  <User className="h-4 w-4 shrink-0 text-white/50" aria-hidden="true" />
                ) : (
                  <Users className="h-4 w-4 shrink-0 text-white/50" aria-hidden="true" />
                )}
                <span className="min-w-0 flex-1">
                  {/* The stored name for every kind. A personal team used to
                      render a fixed "Personal" label, which made the name
                      uneditable in practice however editable the row was. The
                      User icon beside it still marks which team is personal. */}
                  <span className="block truncate text-sm text-white">{team.name}</span>
                  <span className="block text-xs font-medium text-white/45">
                    {t('settingsModal.teams.teamMeta', {
                      role: t(`settingsModal.teams.role.${team.myRole}`),
                      count: team.memberCount,
                    })}
                  </span>
                </span>
                {isActive && (
                  <span
                    data-testid={`teams-list-active-${team.id}`}
                    className="shrink-0 rounded-md bg-white/[0.12] px-1.5 py-0.5 text-xs font-medium text-white/70"
                  >
                    {t('settingsModal.teams.activeBadge')}
                  </span>
                )}
              </button>
            )
          })}
        </Card>
        <div className="mt-2 flex justify-end">
          <button
            type="button"
            data-testid="teams-create-open"
            onClick={openCreate}
            className="inline-flex cursor-pointer items-center gap-1.5 text-sm text-white/60 transition-colors hover:text-white"
          >
            <Plus className="h-4 w-4" />
            {t('settingsModal.teams.createTeam')}
          </button>
        </div>
      </div>

      {showRename && <TeamNameField team={selectedTeam} />}

      {/* Two notices, because both surviving D10 rules are OWNER-scoped. The
          single notice this replaces told everyone "it cannot be deleted and
          you cannot leave it" — false for an invited member, and contradicted
          three elements down by the Leave button `canLeaveTeam` correctly
          renders for them. */}
      {selectedTeam.kind === 'personal' && (
        <p className="text-sm leading-6 text-white/55" data-testid="teams-personal-notice">
          {t(
            selectedTeam.myRole === 'owner'
              ? 'settingsModal.teams.personalTeamNotice'
              : 'settingsModal.teams.personalTeamGuestNotice',
          )}
        </p>
      )}

      {/* The roster is no longer swapped out for the notice on a personal team.
          It has one now, and the people in it need to be manageable. */}
      <TeamMembersList
        team={selectedTeam}
        members={members.data ?? []}
        isLoading={members.isLoading}
        currentUserId={user?.id ?? null}
      />

      {showInvites && (
        <TeamInvitePanel team={selectedTeam} analyticsEntryPoint={mountEntryPointRef.current} />
      )}

      {/* Keyed so switching teams starts back on the newest page. */}
      {canViewTeamCredits(selectedTeam) && !postpaid && (
        <TeamCreditHistory key={selectedTeam.id} teamId={selectedTeam.id} />
      )}

      {showBuyCredits && (
        <div className="flex justify-end">
          <SecondaryButton testId="teams-buy-credits" onClick={() => open('usage-credits', { billingTeamId: selectedTeam.id })}>
            {t('settingsModal.teams.buyCredits')}
          </SecondaryButton>
        </div>
      )}

      {showUsage && (
        <div className="flex justify-end">
          <SecondaryButton testId="teams-view-usage" onClick={() => open('usage-credits', { billingTeamId: selectedTeam.id })}>
            {t('settingsModal.teams.viewUsage')}
          </SecondaryButton>
        </div>
      )}

      {(showLeave || showDelete) && (
        <div data-testid="teams-danger-zone">
          <SectionLabel>{t('settingsModal.teams.dangerZone')}</SectionLabel>
          <div className="flex flex-wrap gap-2">
            {showLeave && (
              <SecondaryButton testId="teams-leave-open" onClick={() => setView('leave')}>
                {t('settingsModal.teams.leaveTeam')}
              </SecondaryButton>
            )}
            {showDelete && (
              <SecondaryButton testId="teams-delete-open" onClick={() => setView('delete')}>
                {t('settingsModal.teams.deleteTeam')}
              </SecondaryButton>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
